<template>
  <section
    class="py-12 lg:py-40"
    :class="wrapperClass">
    <div class="mx-auto max-w-6xl px-4">
      <!-- Optional title above the video -->
      <div
        v-if="title"
        class="pb-2 pt-8">
        <h3
          :class="[
            'text-left text-[28px] font-semibold leading-tight sm:text-4xl md:text-5xl lg:pb-[3rem] lg:text-6xl',
            theme === 'dark' ? 'text-white' : 'text-black'
          ]">
          {{ title }}
        </h3>
      </div>

      <!-- Phone frame layout -->
      <div
        v-if="showInPhone"
        class="flex justify-center py-4">
        <!-- Wrapper for @tailwindcss/aspect-ratio padding trick -->
        <div class="aspect-h-9 aspect-w-16">
          <div class="o7-phone relative w-full">
            <!-- Device body / frame -->
            <div :class="['absolute inset-0 rounded-[44px] shadow-2xl transition-colors duration-500', frameClass]" />
            <!-- Screen -->
            <div class="absolute inset-[12px] overflow-hidden rounded-[36px] bg-black">
              <!-- Dynamic island / notch -->
              <div
                class="o7-notch absolute left-0 top-1/2 z-50 ml-[8px] h-[120px] w-[26px] -translate-y-1/2 rounded-r-[18px] bg-black shadow-[2px_0_8px_rgba(0,0,0,0.4)]"
                aria-hidden="true" />
              <!-- Video content -->
              <div class="absolute inset-0">
                <YoutubePlayer
                  v-if="firstVideo && firstVideo.youtube_id"
                  :index="0"
                  :video-id="firstVideo.youtube_id || ''"
                  :image-path="firstVideoCover" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Plain layout (no phone frame) -->
      <div
        v-else
        class="py-6">
        <!-- Wrapper for @tailwindcss/aspect-ratio padding trick -->
        <div class="aspect-h-9 aspect-w-16">
          <div class="w-full overflow-hidden rounded-xl">
            <YoutubePlayer
              v-if="firstVideo && firstVideo.youtube_id"
              :index="0"
              :video-id="firstVideo.youtube_id || ''"
              :image-path="firstVideoCover" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { computed } from 'vue'

  import YoutubePlayer from '~/components/youtube-player.vue'

  type ImageObject = { url?: string; path?: string; permalink?: string; src?: string }
  type ImageInput = string | ImageObject | ImageObject[] | null | undefined

  type YoutubeItem = {
    youtube_id: string
    title: string
    cover_photo?: ImageInput
    thumbnail?: string
  }

  type YoutubeVideoElement = {
    header?: { title?: string }
    title?: string
    background_color?: string
    show_in_phone?: boolean
    youtube_items?: YoutubeItem[]
  }

  const props = withDefaults(defineProps<{ element?: YoutubeVideoElement }>(), {})
  const el = computed(() => props.element)

  function resolveImage(input: ImageInput): string | undefined {
    if (!input) {
      return undefined
    }
    if (typeof input === 'string') {
      return input
    }
    if (Array.isArray(input)) {
      const first = input[0]
      return first?.url || first?.path || first?.permalink || first?.src || undefined
    }
    return input.url || input.path || input.permalink || input.src || undefined
  }

  const youtubeItems = computed<YoutubeItem[]>(() => {
    const items = el.value?.youtube_items
    return Array.isArray(items) ? items : []
  })

  const firstVideo = computed<YoutubeItem | null>(() => youtubeItems.value[0] || null)
  const firstVideoCover = computed<string | undefined>(() => resolveImage(firstVideo.value?.cover_photo) || '')

  const title = computed<string>(() => el.value?.header?.title || el.value?.title || '')

  const theme = computed<'dark' | 'light'>(() => (el.value?.background_color === 'dark' ? 'dark' : 'light'))

  const wrapperClass = computed(() => (theme.value === 'dark' ? 'bg-black' : 'bg-white'))

  const frameClass = computed(() => (theme.value === 'dark' ? 'iphone-frame-dark' : 'iphone-frame-light'))

  const showInPhone = computed<boolean>(() => {
    const v = el.value?.show_in_phone
    return typeof v === 'boolean' ? v : true
  })
</script>

<style scoped>
  .o7-phone {
    filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.35));
  }

  .iphone-frame-light {
    background: linear-gradient(145deg, #f5f5f7 0%, #dcdde1 15%, #f0f0f2 40%, #b8b8b9 60%, #e6e7e8 80%, #fafafa 100%);
  }

  .iphone-frame-dark {
    background: linear-gradient(145deg, #222 0%, #3a3a3d 20%, #111 50%, #2a2a2d 75%, #000 100%);
  }

  .iphone-frame-light::after,
  .iphone-frame-dark::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.4),
      inset 0 -2px 4px rgba(0, 0, 0, 0.3);
    pointer-events: none;
  }

  /* Hide the notch on very small screens (< 480px) */
  @media (max-width: 479px) {
    .o7-notch {
      display: none;
    }
  }
</style>
